import { createAdminClient } from '@/lib/supabase/admin'
import { emailLogger } from '@/lib/logger'

export const FROM_EMAIL = 'Alumnia <alumni@alumnia.net>'

// ============================================================================
// Notification Logging Types and Helper
// ============================================================================

export type NotificationType =
  | 'admin_new_member'
  | 'invitation'
  | 'welcome'
  | 'event_reminder'
  | 'event_confirmation'
  | 'connection_request'
  | 'connection_accepted'
  | 'connection_reminder'
  | 'connection_backfill'
  | 'chapter_invitation'
  | 'org_admin_invitation'
  | 'incomplete_profile_reminder'
  | 'incomplete_profile_warning'
  | 'lead_notification'
  | 'admin_test'
  | 'account_deletion_request'
  | 'account_deletion_cancelled'
  | 'traveler_visiting'
  | 'trip_reminder'
  | 'member_referral'
  | 'weekly_digest'
  | 'opportunity_campaign'
  | 'tier_warning'
  | 'tier_limit_reached'
  | 'tier_change'
  | 'payment_failed'
  // New notification system types
  | 'feed_comment'
  | 'feed_reaction'
  | 'dm_received'
  | 'admin_post'
  | 'email_change_notice'
  // Apple G 1.2 — UGC safety
  | 'content_reported'
  | 'user_blocked'
  | 'mentorship_outreach'
  | 'mentorship_matched'
  | 'mentorship_review_pending'
  | 'mentorship_no_match'
  | 'graduation_confirm'
  | 'graduation_live'

export interface LogNotificationParams {
  type: NotificationType
  recipientEmail: string
  recipientName?: string
  organizationId?: string
  status: 'sent' | 'failed' | 'pending'
  errorMessage?: string
  metadata?: Record<string, unknown>
  resendMessageId?: string
}

/**
 * Log a notification attempt to the database
 * Uses admin client to bypass RLS
 */
export async function logNotification(params: LogNotificationParams): Promise<void> {
  try {
    const supabaseAdmin = createAdminClient()
    const payload = {
      notification_type: params.type,
      recipient_email: params.recipientEmail,
      recipient_name: params.recipientName || null,
      organization_id: params.organizationId || null,
      status: params.status,
      error_message: params.errorMessage || null,
      metadata: params.metadata || {},
      resend_message_id: params.resendMessageId || null,
      sent_at: params.status === 'sent' ? new Date().toISOString() : null,
    }

    // When we have a Resend message id, use UPSERT with ON CONFLICT DO NOTHING so
    // idempotent Resend retries (which return the same `message.id` without
    // actually re-sending the email) don't create duplicate rows AND don't
    // overwrite the original `sent_at`. The email was physically sent exactly
    // once — at the time of the first log call — so the original row is the
    // canonical record and nothing about it should change on retry. Falls back
    // to a plain INSERT when there's no message id (e.g. failed sends), since
    // the NULLable column isn't in the unique index.
    //
    // IMPORTANT: the underlying `idx_notification_logs_resend_id` unique index
    // must remain NON-PARTIAL (no WHERE clause). PostgREST's `.upsert()`
    // cannot target a partial unique index — Postgres would raise
    // "no unique or exclusion constraint matching the ON CONFLICT specification".
    // The index NULL semantics still allow multiple failed-send rows because
    // plain unique indexes treat NULLs as distinct by default.
    const { error } = params.resendMessageId
      ? await supabaseAdmin
          .from('notification_logs')
          .upsert(payload, { onConflict: 'resend_message_id', ignoreDuplicates: true })
      : await supabaseAdmin.from('notification_logs').insert(payload)

    if (error) {
      // Don't throw - logging failures shouldn't break email sending
      emailLogger.error({ error: error.message }, 'Failed to log notification')
    }
  } catch (error) {
    // Silent fail - logging is non-critical
    emailLogger.error({ error: (error as Error).message }, 'Exception logging notification')
  }
}

/**
 * Bulk variant of {@link logNotification} — one round-trip for a whole batch of
 * sends (the weekly-digest cron logs up to 50 recipients per chunk). Uses the
 * same UPSERT-on-`resend_message_id` semantics: idempotent Resend retries that
 * return the same ids don't create duplicate rows, while rows with a NULL
 * `resend_message_id` (failed sends, or non-attributable batch ids) are treated
 * as distinct by the non-partial unique index and insert cleanly.
 */
export async function logNotificationsBatch(paramsList: LogNotificationParams[]): Promise<void> {
  if (paramsList.length === 0) return
  try {
    const supabaseAdmin = createAdminClient()
    const rows = paramsList.map((params) => ({
      notification_type: params.type,
      recipient_email: params.recipientEmail,
      recipient_name: params.recipientName || null,
      organization_id: params.organizationId || null,
      status: params.status,
      error_message: params.errorMessage || null,
      metadata: params.metadata || {},
      resend_message_id: params.resendMessageId || null,
      sent_at: params.status === 'sent' ? new Date().toISOString() : null,
    }))

    const { error } = await supabaseAdmin
      .from('notification_logs')
      .upsert(rows, { onConflict: 'resend_message_id', ignoreDuplicates: true })

    if (error) {
      emailLogger.error(
        { error: error.message, count: rows.length },
        'Failed to log notification batch'
      )
    }
  } catch (error) {
    emailLogger.error({ error: (error as Error).message }, 'Exception logging notification batch')
  }
}

// Re-export the pure base-URL helper so senders can keep importing `getBaseUrl`
// from './shared' without dragging admin client / logger into template bundles.
export { getEmailBaseUrl as getBaseUrl } from '@/components/emails/shared/base-url'
